import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Paper,
  InputAdornment,
  Autocomplete,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  CalendarToday,
  Person,
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  ChatBubbleOutline as ChatIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import api from "../../../api/api";
import { createFormDateTime } from "../../../../utils/timezoneUtils";
import AssetReturnModal from "./AssetReturnModal";
import DealReturnNotice from "./DealReturnNotice";
import BankRefundsPanel from "./BankRefundsPanel";
import RentalHistoryDetails from "./RentalHistoryDetails";

// Функции для работы со статусами сделок
const getStatusLabel = (status) => {
  const statusLabels = {
    в_процессе: "В процессе",
    просрочена: "Просрочена",
    закрыта: "Закрыта",
  };
  return statusLabels[status] || status || "В процессе";
};

const getStatusChipStyle = (status) => {
  const statusStyles = {
    в_процессе: {
      backgroundColor: "#fff3cd",
      color: "#856404",
    },
    просрочена: {
      backgroundColor: "#f8d7da",
      color: "#721c24",
    },
    закрыта: {
      backgroundColor: "#d1ecf1",
      color: "#0c5460",
    },
  };
  return statusStyles[status] || statusStyles["в_процессе"];
};

const savedAssetIds = (deal) => {
  if (Array.isArray(deal.rental_assets)) return deal.rental_assets.map((row) => row.asset_id);
  if (Array.isArray(deal.asset_ids)) return deal.asset_ids;
  return deal.asset_id ? [deal.asset_id] : [];
};

const RentalDealsEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const isNewDeal = id === "new";
  const [chatOpening, setChatOpening] = useState(false);
  const chatOpeningRef = useRef(false);
  const [savedClientId, setSavedClientId] = useState(null);

  const openClientChat = async () => {
    if (isNewDeal || chatOpeningRef.current || !savedClientId || String(savedClientId) !== String(formData.client_id)) return;
    chatOpeningRef.current = true;
    setChatOpening(true);
    try {
      const { data } = await api.post(`/chats/for-deal/${id}`);
      if (!Number.isInteger(data?.id) || data.id < 1) throw new Error('Invalid chat');
      navigate(`/messenger?chat=${data.id}`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      enqueueSnackbar(typeof detail === 'string' ? detail : 'Не удалось открыть чат клиента. Попробуйте ещё раз.', { variant: 'error' });
    } finally {
      chatOpeningRef.current = false;
      setChatOpening(false);
    }
  };

  const [formData, setFormData] = useState({
    deal_number: "",
    start_date: createFormDateTime(),
    end_date: isNewDeal ? createFormDateTime() : null, // Для новых сделок сразу заполняем тем же временем
    client_id: "",
    asset_ids: [],
    amount: 0,
    amount_due: 0,
    discount_amount: 0,
    discount_type: "amount", // "amount" или "percent"
    status: "в_процессе",
    actual_deposit: 0, // Фактически взятый залог
    deposit_date: null, // Дата внесения залога
  });

  // Проверка, закрыта ли сделка (режим "только для чтения")
  const isClosedDeal =
    formData.status === "закрыта" || formData.status === "отменена";

  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientsError, setClientsError] = useState('');
  const clientQuery = useRef('');
  const clientTimer = useRef(null);
  const clientRequest = useRef(0);
  useEffect(() => () => {
    clearTimeout(clientTimer.current);
    clientRequest.current += 1;
  }, []);
  const [assets, setAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const persistedAssetIds = useRef([]);
  const inventoryRemovalLocked = useRef(false);
  const [payments, setPayments] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState(null); // 💰 Сводка по платежам с балансом
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    date: createFormDateTime(),
    method: "cash",
  });
  const [loading, setLoading] = useState(false);
  const [dealReady, setDealReady] = useState(isNewDeal);
  const [dealLoadError, setDealLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const paymentSavingRef = useRef(false);
  const [rentalHistory, setRentalHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(false);

  // Состояния для пунктов выдачи
  const [rentalPoints, setRentalPoints] = useState([]);
  const [selectedRentalPoint, setSelectedRentalPoint] = useState(null);
  const [rentalPointsLoading, setRentalPointsLoading] = useState(false);

  // Состояние для отслеживания пересчета с сервера
  const [isRecalculated, setIsRecalculated] = useState(false);
  const [datesDirty, setDatesDirty] = useState(false);
  const [itemAdjustments, setItemAdjustments] = useState({});
  const [adjustmentForm, setAdjustmentForm] = useState({
    scope: "all",
    assetIds: [],
    kind: "discount",
    valueType: "amount",
    value: "",
  });
  // Маркер последнего запроса пересчета, чтобы игнорировать устаревшие ответы
  const lastRecalcReqIdRef = useRef(0);

  // Состояние для модального окна возврата актива
  const [returnModalOpen, setReturnModalOpen] = useState(false);
  const [assetToReturn, setAssetToReturn] = useState(null);

  // Функция для получения тарифа из актива
  const getAssetTariff = (asset) => {
    if (asset.selected_tariff) {
      const tariff = asset.selected_tariff;
      let period = "период";
      if (tariff.days > 0) {
        period = tariff.days === 1 ? "день" : `${tariff.days} дн.`;
      } else if (tariff.hours > 0) {
        period = tariff.hours === 1 ? "час" : `${tariff.hours} ч.`;
      } else if (tariff.minutes > 0) {
        period = tariff.minutes === 1 ? "мин" : `${tariff.minutes} мин.`;
      }
      const result = {
        price: tariff.price,
        period: period,
        periodMinutes: tariff.days > 0 ? tariff.days * 1440 : tariff.hours > 0 ? tariff.hours * 60 : tariff.minutes,
        period_type:
          tariff.fixed_price ? "fixed" : tariff.period_type || "period",
        currency: "₽",
        source: "selected_tariff",
      };
      return result;
    }

    if (asset.tariff && asset.tariff.price) {
      const result = {
        price: asset.tariff.price,
        period: asset.tariff.period || asset.tariff.billing_period || "период",
        period_type: asset.tariff.period_type || "period",
        currency: asset.tariff.currency || "₽",
        source: "tariff",
      };
      return result;
    }

    if (asset.rental_tariff && asset.rental_tariff.price) {
      const result = {
        price: asset.rental_tariff.price,
        period:
          asset.rental_tariff.period ||
          asset.rental_tariff.billing_period ||
          "период",
        period_type: asset.rental_tariff.period_type || "period",
        currency: asset.rental_tariff.currency || "₽",
        source: "rental_tariff",
      };
      return result;
    }

    // ✅ ИСПРАВЛЕНИЕ: Если сумма была пересчитана с сервера, не используем total_amount из активов
    if (isRecalculated && !isNewDeal) {
      // Вернуть null чтобы использовать расчет по тарифам
      return null;
    }

    // ✅ ИСПРАВЛЕНИЕ: Для существующих сделок приоритет total_amount, затем rental_price_per_period
    if (!isNewDeal && asset.total_amount != null) {
      return {
        price: asset.total_amount,
        period: "итоговая_сумма", // Это УЖЕ рассчитанная сумма за весь период
        period_type: "period",
        currency: "₽",
        source: "total_amount_fixed",
      };
    }

    // Fallback к rental_price_per_period если total_amount недоступно
    if (asset.rental_price_per_period && !isNewDeal) {
      return {
        price: asset.rental_price_per_period,
        period: "итоговая_сумма",
        period_type: "period",
        currency: "₽",
        source: "rental_price_per_period",
      };
    }

    if (asset.price) {
      const result = {
        price: asset.price,
        period: asset.billing_period || "период",
        period_type: "period",
        currency: "₽",
        source: "price",
      };
      return result;
    }

    if (asset.purchase_price) {
      const result = {
        price: Math.round(asset.purchase_price * 0.1),
        period: "неделя",
        period_type: "period",
        currency: "₽",
        source: "purchase_price_calculated",
      };
      return result;
    }

    return {
      price: 100,
      period: "день",
      period_type: "period",
      currency: "₽",
      source: "default",
    };
  };

  // Расчеты
  const calculations = useMemo(() => {
    if (!formData.start_date || !formData.end_date) {
      return {
        duration: 0,
        weeks: 0,
        totalAmount: 0,
        finalAmount: 0,
        paid: 0,
        remaining: 0,
        lineItems: [],
        itemAdjustmentTotal: 0,
      };
    }

    const startDate = new Date(formData.start_date);
    const endDate = new Date(formData.end_date);
    const timeDiff = endDate.getTime() - startDate.getTime();

    // Правильная логика расчета дней: разность между датами
    // Например: с 24.08 по 31.08 = 7 дней
    let diffDays = Math.ceil(timeDiff / (1000 * 3600 * 24));
    if (diffDays === 0) diffDays = 1; // Минимум 1 день

    const diffHours = Math.ceil(timeDiff / (1000 * 3600)); // Количество часов
    const diffMinutes = Math.ceil(timeDiff / (1000 * 60)); // Количество минут
    const weeks = Math.ceil(diffDays / 7);

    const lineItems = selectedAssets.map((asset) => {
        let assetCost = 0;
        // У существующей позиции уже есть свой сохранённый срок и точная сумма.
        // Общая дата сделки не должна превращать более короткую позицию в длинную.
        if (!isNewDeal && !datesDirty && asset.total_amount != null) {
          assetCost = Number(asset.total_amount) || 0;
        } else {
        const tariff = getAssetTariff(asset);

        // Handle null tariff case
        if (!tariff) {
          assetCost = 100; // Default price
        } else if (tariff.period_type === "fixed") {
          // Фиксированная стоимость независимо от периода
          assetCost = tariff.price;
        } else if (tariff.periodMinutes > 0) {
          assetCost = Number(tariff.price) * Math.max(1, Math.ceil(diffMinutes / tariff.periodMinutes));
        } else {
          // ✅ ИСПРАВЛЕНИЕ: Обрабатываем итоговую сумму для существующих записей
          if (tariff.period === "итоговая_сумма") {
            assetCost = tariff.price; // Это уже рассчитанная сумма за весь период
          } else if (tariff.period === "fixed_price") {
            // ✅ НОВОЕ: Фиксированная цена - не умножаем на период!
            assetCost = tariff.price;
          } else if (
            tariff.period.includes("день") ||
            tariff.period === "день"
          ) {
            assetCost = tariff.price * diffDays;
          } else if (
            tariff.period.includes("неделя") ||
            tariff.period.includes("нед")
          ) {
            assetCost = tariff.price * weeks;
          } else if (tariff.period.includes("час") || tariff.period === "час") {
            // Исправлено: используем фактическое количество часов
            assetCost = tariff.price * diffHours;
          } else if (tariff.period.includes("мин") || tariff.period === "мин") {
            // Исправлено: используем фактическое количество минут
            assetCost = tariff.price * diffMinutes;
          } else {
            assetCost = tariff.price * weeks;
          }
        }
        }
        const discountApplied = Number(
          itemAdjustments[asset.id] ?? asset.discount_applied ?? 0,
        ) || 0;
        return {
          assetId: asset.id,
          name: asset.name || `Товар #${asset.id}`,
          baseAmount: Number(assetCost) || 0,
          discountApplied,
          finalAmount: Math.max(0, (Number(assetCost) || 0) - discountApplied),
          startDate: asset.start_date || formData.start_date,
          endDate: asset.end_date || formData.end_date,
        };
      });

    let totalAmount = lineItems.reduce((sum, item) => sum + item.finalAmount, 0);
    if (datesDirty && isRecalculated && !isNewDeal && formData.amount != null) {
      totalAmount = parseFloat(formData.amount) || 0;
    }

    // 💰 ИСПОЛЬЗУЕМ БАЛАНС ИЗ payment_summary ВМЕСТО СУММЫ ПЛАТЕЖЕЙ
    const totalPaid = isNewDeal || !paymentSummary
      ? payments.reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0)
      : Number(paymentSummary.total_paid) || 0;

    // Применяем скидку/наценку
    let finalAmount = totalAmount;
    const discountAmount = parseFloat(formData.discount_amount) || 0;

    if (formData.discount_type === "percent") {
      // Процентная скидка/наценка
      finalAmount = totalAmount * (1 + discountAmount / 100);
    } else {
      // Фиксированная скидка/наценка (положительное значение = наценка, отрицательное = скидка)
      finalAmount = totalAmount + discountAmount;
    }

    // Расчет общего залога
    const totalDeposit = selectedAssets.reduce((sum, asset) => {
      return sum + (parseFloat(asset.deposit) || 0);
    }, 0);

    return {
      duration: diffDays,
      weeks: weeks,
      totalAmount: totalAmount,
      finalAmount: finalAmount,
      paid: totalPaid,
      remaining: finalAmount - totalPaid,
      totalDeposit: totalDeposit,
      lineItems,
      itemAdjustmentTotal: lineItems.reduce(
        (sum, item) => sum + item.discountApplied,
        0,
      ),
    };
  }, [
    formData.start_date,
    formData.end_date,
    formData.discount_amount,
    formData.discount_type,
    payments,
    selectedAssets,
    isRecalculated,
    datesDirty,
    itemAdjustments,
    formData.amount,
    paymentSummary, // 💰 Добавляем зависимость от payment_summary
  ]);

  // Генерация номера сделки с учетом компании (серверная генерация)
  const generateDealNumber = async () => {
    try {
      // Получаем номер сделки с сервера
      const response = await api.get("/rental-deals/generate-deal-number", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      return response.data.deal_number;
    } catch (error) {
      console.error("Ошибка генерации номера сделки с сервера:", error);

      // Fallback: генерируем временный номер, сервер его заменит при сохранении
      const now = new Date();
      const timestamp = now.getTime().toString().slice(-6); // Последние 6 цифр timestamp
      return `TEMP-${timestamp}`;
    }
  };

  // Функция для получения информации о текущем пользователе
  const fetchCurrentUser = async () => {
    try {
      const response = await api.get("/login/me", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Ошибка получения информации о пользователе:", error);
      // В случае ошибки возвращаем дефолтные значения
      return { company_id: 1, user_id: 1 };
    }
  };

  // Функция для пересчета сделки с сервера при изменении дат
  const recalculateDealFromServer = async () => {
    if (!formData.start_date || !formData.end_date || !id || isNewDeal) {
      return;
    }

    try {
      // Передаем новые даты И текущие выбранные активы для пересчета
      const recalculateData = {
        start_date: formData.start_date,
        end_date: formData.end_date,
        asset_ids: formData.asset_ids, // ✅ Добавляем текущие выбранные активы
      };

      const reqId = ++lastRecalcReqIdRef.current;

      const response = await api.post(
        `/rental-deals/${id}/recalculate`,
        recalculateData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      if (reqId !== lastRecalcReqIdRef.current) {
        return;
      }

      if (response.data.total_amount !== undefined) {
        // ✅ ПРАВИЛЬНАЯ ЛОГИКА: Вычисляем базовую сумму аренды БЕЗ скидки
        // total_amount с сервера - это итоговая сумма ПОСЛЕ скидки
        // Нужно добавить скидку обратно, чтобы получить базовую сумму
        const serverTotalAmount = Number(response.data.total_amount);
        const currentDiscount = Number(formData.discount_amount) || 0;
        let baseAmount = response.data.base_amount == null ? serverTotalAmount : Number(response.data.base_amount);

        if (response.data.base_amount == null && formData.discount_type === "amount") {
          // Если скидка фиксированная, добавляем её к итоговой сумме
          baseAmount = serverTotalAmount - currentDiscount; // currentDiscount отрицательный (-400), поэтому вычитаем
        } else if (response.data.base_amount == null && formData.discount_type === "percent") {
          // Если скидка процентная, пересчитываем базовую сумму
          const discountMultiplier = 1 + currentDiscount / 100;
          baseAmount = discountMultiplier === 0 ? Number(formData.amount) : serverTotalAmount / discountMultiplier;
        }

        if (!Number.isFinite(baseAmount)) return;

        setIsRecalculated(true);
        setFormData((prev) => ({
          ...prev,
          amount: baseAmount,
        }));
      }
    } catch (error) {
      console.error("Ошибка пересчета с сервера:", error);
    }
  };

  // Форматирование даты для input datetime-local
  const formatDateTimeLocal = (date) => {
    if (!date) return "";
    const d = new Date(date);

    // Получаем локальное время без конвертации в UTC
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");

    const localString = `${year}-${month}-${day}T${hours}:${minutes}`;

    return localString;
  };

  // Парсинг даты из input datetime-local
  const parseDateTimeLocal = (value) => {
    if (!value) return null;
    return new Date(value);
  };

  // Получение timezone из выбранного пункта выдачи
  const getRentalPointTimezone = () => {
    // Сначала проверяем выбранный пункт выдачи
    if (selectedRentalPoint?.timezone) {
      return selectedRentalPoint.timezone;
    }

    // Если пункт не выбран, пытаемся получить из первого актива
    if (selectedAssets.length > 0) {
      const firstAsset = selectedAssets[0];
      const timezone = firstAsset?.rental_point?.timezone;

      return timezone || getUserBrowserTimezone();
    }

    return getUserBrowserTimezone(); // дефолт по временной зоне браузера
  };

  // Функция конвертации времени между временными зонами
  const convertTimeBetweenTimezones = (date, fromTimezone, toTimezone) => {
    if (!date || fromTimezone === toTimezone) return date;

    const timezoneOffsets = {
      "Europe/Kaliningrad": 2, // UTC+2
      "Europe/Moscow": 3, // UTC+3
      "Europe/Samara": 4, // UTC+4
      "Asia/Yekaterinburg": 5, // UTC+5
      "Asia/Omsk": 6, // UTC+6
      "Asia/Krasnoyarsk": 7, // UTC+7
      "Asia/Novosibirsk": 7, // UTC+7 (такой же как Красноярск)
      "Asia/Irkutsk": 8, // UTC+8
      "Asia/Yakutsk": 9, // UTC+9
      "Asia/Vladivostok": 10, // UTC+10
      "Asia/Magadan": 11, // UTC+11
      "Asia/Kamchatka": 12, // UTC+12
    };

    const fromOffset = timezoneOffsets[fromTimezone] || 5;
    const toOffset = timezoneOffsets[toTimezone] || 5;
    const diffHours = toOffset - fromOffset;

    const newDate = new Date(date);
    newDate.setHours(newDate.getHours() + diffHours);

    return newDate;
  };

  // Функция для получения временной зоны браузера пользователя
  const getUserBrowserTimezone = () => {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Маппинг временных зон браузера на наши поддерживаемые зоны России
    const timezoneMapping = {
      "Europe/Kaliningrad": "Europe/Kaliningrad",
      "Europe/Moscow": "Europe/Moscow",
      "Europe/Samara": "Europe/Samara",
      "Asia/Yekaterinburg": "Asia/Yekaterinburg",
      "Asia/Omsk": "Asia/Omsk",
      "Asia/Krasnoyarsk": "Asia/Krasnoyarsk",
      "Asia/Novosibirsk": "Asia/Novosibirsk",
      "Asia/Irkutsk": "Asia/Irkutsk",
      "Asia/Yakutsk": "Asia/Yakutsk",
      "Asia/Vladivostok": "Asia/Vladivostok",
      "Asia/Magadan": "Asia/Magadan",
      "Asia/Kamchatka": "Asia/Kamchatka",
    };

    const mappedTimezone =
      timezoneMapping[browserTimezone] || "Asia/Yekaterinburg"; // Fallback на Екатеринбург если зона не поддерживается

    return mappedTimezone;
  };

  // Сохраняем предыдущую временную зону для конвертации
  const [previousTimezone, setPreviousTimezone] = useState(() =>
    getUserBrowserTimezone(),
  ); // Инициализируем по временной зоне браузера

  // Эффект для пересчета времени при смене пункта выдачи
  useEffect(() => {
    if (selectedRentalPoint?.timezone) {
      const currentDefaultTimezone = getUserBrowserTimezone();

      // Если это первый выбор пункта (previousTimezone === зона браузера) и выбранная зона не совпадает с зоной браузера
      const isFirstSelection =
        previousTimezone === currentDefaultTimezone &&
        selectedRentalPoint.timezone !== currentDefaultTimezone;

      // Если это смена пункта (не первый выбор) и зоны разные
      const isTimezoneChange =
        previousTimezone !== currentDefaultTimezone &&
        selectedRentalPoint.timezone !== previousTimezone;

      if (isFirstSelection || isTimezoneChange) {
        console.log("🔄 Пересчет времени при смене пункта:", {
          from: previousTimezone,
          to: selectedRentalPoint.timezone,
          browserTimezone: currentDefaultTimezone,
          isFirstSelection: isFirstSelection,
          isTimezoneChange: isTimezoneChange,
        });

        // Пересчитываем время начала
        if (formData.start_date) {
          const newStartDate = convertTimeBetweenTimezones(
            formData.start_date,
            previousTimezone,
            selectedRentalPoint.timezone,
          );

          setFormData((prev) => ({
            ...prev,
            start_date: newStartDate,
          }));
        }

        // Пересчитываем время окончания
        if (formData.end_date) {
          const newEndDate = convertTimeBetweenTimezones(
            formData.end_date,
            previousTimezone,
            selectedRentalPoint.timezone,
          );

          setFormData((prev) => ({
            ...prev,
            end_date: newEndDate,
          }));
        }

        // Пересчитываем время платежа
        if (paymentForm.date) {
          const newPaymentDate = convertTimeBetweenTimezones(
            paymentForm.date,
            previousTimezone,
            selectedRentalPoint.timezone,
          );

          setPaymentForm((prev) => ({
            ...prev,
            date: newPaymentDate,
          }));
        }
      }

      // Обновляем предыдущую временную зону
      setPreviousTimezone(selectedRentalPoint.timezone);
    }
  }, [selectedRentalPoint?.timezone]);

  // Конвертация даты в ISO строку с учетом timezone точки аренды
  const convertDateToTimezoneISO = (date) => {
    if (!date) return null;

    const targetTimezone = getRentalPointTimezone();
    const userBrowserTimezone = getUserBrowserTimezone();

    // Сначала конвертируем время из браузерной зоны в целевую зону пункта выдачи
    const convertedDate = convertTimeBetweenTimezones(
      date,
      userBrowserTimezone,
      targetTimezone,
    );

    // Получаем смещение часового пояса для целевой зоны
    const getTimezoneOffset = (tz) => {
      const timezoneOffsets = {
        "Europe/Kaliningrad": "+02:00", // UTC+2
        "Europe/Moscow": "+03:00", // UTC+3
        "Europe/Samara": "+04:00", // UTC+4
        "Asia/Yekaterinburg": "+05:00", // UTC+5
        "Asia/Omsk": "+06:00", // UTC+6
        "Asia/Krasnoyarsk": "+07:00", // UTC+7
        "Asia/Novosibirsk": "+07:00", // UTC+7 (такой же как Красноярск)
        "Asia/Irkutsk": "+08:00", // UTC+8
        "Asia/Yakutsk": "+09:00", // UTC+9
        "Asia/Vladivostok": "+10:00", // UTC+10
        "Asia/Magadan": "+11:00", // UTC+11
        "Asia/Kamchatka": "+12:00", // UTC+12
      };
      return timezoneOffsets[tz] || "+05:00"; // По умолчанию Екатеринбург
    };

    // Используем сконвертированное время для формирования ISO строки
    const year = convertedDate.getFullYear();
    const month = convertedDate.getMonth();
    const day = convertedDate.getDate();
    const hours = convertedDate.getHours();
    const minutes = convertedDate.getMinutes();
    const seconds = convertedDate.getSeconds();
    const milliseconds = convertedDate.getMilliseconds();

    // Получаем правильное смещение часового пояса для целевой зоны
    const timezoneOffset = getTimezoneOffset(targetTimezone);

    // Создаем ISO строку с правильным часовым поясом
    const monthStr = String(month + 1).padStart(2, "0");
    const dayStr = String(day).padStart(2, "0");
    const hoursStr = String(hours).padStart(2, "0");
    const minutesStr = String(minutes).padStart(2, "0");
    const secondsStr = String(seconds).padStart(2, "0");
    const msStr = String(milliseconds).padStart(3, "0");

    // Формируем ISO строку с часовым поясом: YYYY-MM-DDTHH:mm:ss.sss+03:00
    const isoString = `${year}-${monthStr}-${dayStr}T${hoursStr}:${minutesStr}:${secondsStr}.${msStr}${timezoneOffset}`;

    console.log("📅 Конвертация даты для сохранения:", {
      originalDate: date.toString(),
      originalTimezone: userBrowserTimezone,
      targetTimezone: targetTimezone,
      convertedDate: convertedDate.toString(),
      timezoneOffset: timezoneOffset,
      localTime: `${year}-${monthStr}-${dayStr} ${hoursStr}:${minutesStr}:${secondsStr}`,
      isoWithTimezone: isoString,
      oldDirectMethod: date.toISOString(),
    });

    return isoString;
  };

  // Загрузка данных при монтировании
  useEffect(() => {
    const initializeNewDeal = async () => {
      if (isNewDeal) {
        const newDealNumber = await generateDealNumber();
        setFormData((prev) => ({ ...prev, deal_number: newDealNumber }));
      }
    };

    initializeNewDeal();
    loadClients();
    loadRentalPoints();
    loadAssets();

    const handleMessage = async (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === "CLIENT_CREATED") {
        console.log("🎉 Получено сообщение о создании клиента:", event.data);

        // Перезагружаем список клиентов
        await loadClients();

        // Если есть ID клиента, автоматически выбираем его
        if (event.data.clientId) {
          // Загружаем данные клиента и выбираем его
          try {
            const response = await api.get(`/clients/${event.data.clientId}`, {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            });
            const client = response.data;
            console.log(
              "✅ Автоматически выбираем созданного клиента:",
              client,
            );
            setSelectedClient(client);
            setFormData((prev) => ({
              ...prev,
              client_id: client.id,
            }));
          } catch (error) {
            console.error("❌ Ошибка загрузки созданного клиента:", error);
          }
        } else if (event.data.clientData) {
          // Если переданы данные клиента напрямую
          const client = event.data.clientData;
          console.log("✅ Автоматически выбираем клиента из данных:", client);
          setSelectedClient(client);
          setFormData((prev) => ({
            ...prev,
            client_id: client.id,
          }));
        }
      }
    };

    window.addEventListener("message", handleMessage);

    if (!isNewDeal) {
      loadDeal();
      loadRentalHistory();
    }

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [id, isNewDeal]);

  // Автоматический пересчет при изменении дат И АКТИВОВ (только для существующих сделок)
  useEffect(() => {
    console.log("🔄 useEffect пересчета дат/активов:");
    console.log("🔄 isNewDeal:", isNewDeal);
    console.log("🔄 start_date:", formData.start_date);
    console.log("🔄 end_date:", formData.end_date);
    console.log("🔄 selectedAssets.length:", selectedAssets.length);
    console.log(
      "🔄 selectedAssets IDs:",
      selectedAssets.map((a) => a.id),
    );

    // Добавляем небольшую задержку для избежания частых запросов
    const timeoutId = setTimeout(() => {
      if (
        !isNewDeal &&
        formData.start_date &&
        formData.end_date &&
        selectedAssets.length > 0
      ) {
        console.log(
          "🔄 Вызываем recalculateDealFromServer (причина: изменение дат или активов)",
        );
        recalculateDealFromServer();
      } else {
        console.log(
          "🔄 НЕ вызываем recalculateDealFromServer - не все условия выполнены",
        );
      }
    }, 500); // Задержка 500мс

    return () => clearTimeout(timeoutId);
  }, [formData.start_date, formData.end_date, selectedAssets, isNewDeal]);

  // Загрузка клиентов
  const loadClients = async () => {
    const version = ++clientRequest.current;
    setClientsLoading(true);
    setClientsError('');
    try {
      // Поиск выполняется сервером до пагинации по всей компании.
      const response = await api.get("/clients/", {
        skipLoading: true,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: {
          limit: 50,
          search: clientQuery.current.trim() || undefined,
          sort_by: "created_at",
          sort_order: "desc", // Новые клиенты первыми
        },
      });
      // API возвращает { items: [...], total, page, page_size, total_pages }
      const clientsData = response.data?.items || response.data || [];
      if (version === clientRequest.current) setClients(Array.isArray(clientsData) ? clientsData : []);
    } catch (error) {
      if (version === clientRequest.current) {
        setClients([]);
        setClientsError('Не удалось найти клиентов. Попробуйте ввести фамилию ещё раз.');
      }
    } finally {
      if (version === clientRequest.current) setClientsLoading(false);
    }
  };

  // Загрузка пунктов выдачи
  const loadRentalPoints = async () => {
    try {
      setRentalPointsLoading(true);
      const response = await api.get("/rental-points/", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setRentalPoints(response.data || []);

      // Если это новая сделка и есть только один пункт, выбираем его автоматически
      if (isNewDeal && response.data && response.data.length === 1) {
        setSelectedRentalPoint(response.data[0]);
      }
    } catch (error) {
      console.error("Ошибка загрузки пунктов выдачи:", error);
    } finally {
      setRentalPointsLoading(false);
    }
  };

  // Загрузка активов
  const loadAssets = async () => {
    try {
      setAssetsLoading(true);
      // Используем обычный эндпоинт для получения всех активов (limit=1000 чтобы загрузить все)
      const response = await api.get("/assets/rental", {
        params: { limit: 1000 },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      // Извлекаем данные из пагинированного ответа
      const assetsData = response.data?.items || response.data || [];

      console.log("🔧 ОТЛАДКА: Загруженные активы:", response.data);
      console.log("🔧 ОТЛАДКА: Количество активов:", assetsData.length);

      // Выводим информацию о тарифах для первых 3 активов
      if (assetsData && assetsData.length > 0) {
        assetsData.slice(0, 3).forEach((asset, index) => {
          console.log(`🔧 ОТЛАДКА: Актив ${index + 1} (${asset.name}):`);
          console.log(`  - selected_tariff:`, asset.selected_tariff);
          console.log(`  - tariff:`, asset.tariff);
          console.log(`  - rental_tariff:`, asset.rental_tariff);
          console.log(`  - price:`, asset.price);
          console.log(`  - purchase_price:`, asset.purchase_price);

          const tariff = getAssetTariff(asset);
          console.log(`  - Итоговый тариф:`, tariff);
        });
      }

      setAssets(assetsData);
    } catch (error) {
      console.error("Ошибка загрузки активов:", error);
    } finally {
      setAssetsLoading(false);
    }
  };

  // Загрузка данных сделки
  const loadDeal = async () => {
    let inventoryComplete = true;
    setDealReady(false);
    setDealLoadError(false);
    try {
      setLoading(true);
      console.log(`Загрузка сделки ID: ${id}`);

      // Сбрасываем флаг пересчета при загрузке сделки
      setIsRecalculated(false);

      const response = await api.get(`/rental-deals/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      console.log("Загруженная сделка:", response.data);
      const deal = response.data;
      setSavedClientId(deal.client_id);
      inventoryRemovalLocked.current = deal.status !== 'заявка' ||
        Number(deal.payment_summary?.total_paid || 0) > 0;
      persistedAssetIds.current = savedAssetIds(deal);

      // 🔍 ОТЛАДКА: Проверяем поля скидки
      console.log("🔍 СКИДКА ИЗ БД:", {
        discount_amount: deal.discount_amount,
        discount_type: deal.discount_type,
        amount: deal.amount,
        amount_due: deal.amount_due,
      });

      // 🔍 ДОПОЛНИТЕЛЬНАЯ ОТЛАДКА: Все поля deal
      console.log("🔍 ВСЕ ПОЛЯ СДЕЛКИ ИЗ БД:", Object.keys(deal));
      console.log("🔍 ПОЛНЫЙ ОБЪЕКТ СДЕЛКИ:", deal);

      // Загружаем активы сделки из новой таблицы rental_asset_deals
      try {
        const assetDealsResponse = await api.get(
          `/rental-asset-deals/deal/${id}/detailed`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        console.log("=== НОВАЯ СИСТЕМА АКТИВОВ ===");
        console.log("Загруженные asset_deals:", assetDealsResponse.data);

        if (assetDealsResponse.data && assetDealsResponse.data.length > 0) {
          // Используем новые данные с полной информацией о клиенте и активах
          const assetDeals = assetDealsResponse.data;
          const assetIds = assetDeals.map((ad) => ad.asset_id);
          persistedAssetIds.current = assetIds;
          console.log("Asset IDs из rental_asset_deals:", assetIds);

          // Сохраняем детальную информацию для отображения
          const mappedAssets = assetDeals.map((ad) => ({
            id: ad.asset_id,
            asset_deal_id: ad.id,
            name: ad.asset_name,
            sku: ad.asset_sku,
            category: ad.asset_category,
            deposit: ad.asset_deposit, // Базовый залог актива
            deposit_amount: ad.deposit_amount, // Залог из asset deal
            rental_price_per_period: ad.rental_price_per_period,
            total_amount: ad.total_amount,
            discount_applied: ad.discount_applied || 0,
            adjusted_total_amount: ad.adjusted_total_amount,
            paid_amount: ad.paid_amount,
            returned_date: ad.returned_date,
            return_comment: ad.return_comment,
            status: ad.status,
            client_name: ad.client_name,
            client_phone: ad.client_phone,
            client_contact_info: ad.client_contact_info,
            start_date: ad.start_date,
            end_date: ad.end_date,
          }));

          setSelectedAssets(mappedAssets);
          setItemAdjustments(
            Object.fromEntries(
              mappedAssets.map((asset) => [asset.id, Number(asset.discount_applied) || 0]),
            ),
          );
          setAdjustmentForm((prev) => ({ ...prev, assetIds: [] }));
          setDatesDirty(false);

          console.log("🔄 Загружены детальные данные об активах:", assetDeals);
          console.log(
            "📋 Маппированные активы с клиентской информацией:",
            mappedAssets,
          );
          console.log(
            "� ЗАЛОГИ активов:",
            mappedAssets.map((a) => ({
              id: a.id,
              name: a.name,
              deposit: a.deposit,
              deposit_amount: a.deposit_amount,
            })),
          );
          console.log("�👤 Информация о клиенте для первого актива:", {
            client_name: mappedAssets[0]?.client_name,
            client_phone: mappedAssets[0]?.client_phone,
            client_contact_info: mappedAssets[0]?.client_contact_info,
          });

          // Получаем client_id из rental_asset_deals (он более актуальный)
          const realClientId = assetDeals[0]?.client_id;

          // Обновляем formData с правильным client_id и asset_ids
          setFormData({
            deal_number: deal.deal_number || "",
            start_date: deal.start_date
              ? new Date(deal.start_date)
              : createFormDateTime(),
            end_date: deal.end_date ? new Date(deal.end_date) : null,
            client_id: realClientId || deal.client_id || "",
            asset_ids: assetIds,
            amount: deal.amount || 0,
            amount_due: deal.amount_due || 0,
            discount_amount: deal.discount_amount || 0,
            discount_type: deal.discount_type || "amount",
            status: deal.status || "в_процессе",
            actual_deposit: deal.actual_deposit || 0,
            deposit_date: deal.deposit_date || null,
          });

          // 🔍 ОТЛАДКА: Проверяем установленные данные скидки
          console.log("🔍 УСТАНОВКА FORMDATA (новая система):", {
            deal_discount_amount: deal.discount_amount,
            deal_discount_type: deal.discount_type,
            setting_discount_amount: deal.discount_amount || 0,
            setting_discount_type: deal.discount_type || "amount",
          });

          // Загружаем клиента с правильным ID
          if (realClientId) {
            await loadClientById(realClientId);
          } else if (deal.client_id) {
            await loadClientById(deal.client_id);
          }
        } else {
          // Fallback к старому методу
          const assetIds = savedAssetIds(deal);

          // Загружаем активы по их ID
          if (assetIds.length > 0) {
            await loadAssetsByIds(assetIds);
          } else {
            setSelectedAssets([]);
          }

          setFormData({
            deal_number: deal.deal_number || "",
            start_date: deal.start_date
              ? new Date(deal.start_date)
              : createFormDateTime(),
            end_date: deal.end_date ? new Date(deal.end_date) : null,
            client_id: deal.client_id || "",
            asset_ids: assetIds,
            amount: deal.amount || 0,
            amount_due: deal.amount_due || 0,
            discount_amount: deal.discount_amount || 0,
            discount_type: deal.discount_type || "amount",
            status: deal.status || "в_процессе",
            actual_deposit: deal.actual_deposit || 0,
            deposit_date: deal.deposit_date || null,
          });

          // 🔍 ОТЛАДКА: Проверяем установленные данные скидки (fallback)
          console.log("🔍 УСТАНОВКА FORMDATA (fallback система):", {
            deal_discount_amount: deal.discount_amount,
            deal_discount_type: deal.discount_type,
            setting_discount_amount: deal.discount_amount || 0,
            setting_discount_type: deal.discount_type || "amount",
          });

          // 👤 Загружаем клиента (fallback)
          if (deal.client_id) {
            await loadClientById(deal.client_id);
          }
        }
      } catch (assetDealsError) {
        inventoryComplete = false;
        setDealLoadError(true);
        console.warn(
          "Ошибка загрузки asset_deals, используем старый метод:",
          assetDealsError,
        );

        // Fallback на старый метод (для совместимости)
        const assetIds = savedAssetIds(deal);

        console.log("=== СТАРАЯ СИСТЕМА АКТИВОВ (FALLBACK) ===");
        console.log("deal.rental_assets:", deal.rental_assets);
        console.log("deal.asset_ids:", deal.asset_ids);
        console.log("deal.asset_id (старое поле):", deal.asset_id);
        console.log("Извлеченные asset_ids:", assetIds);

        setFormData({
          deal_number: deal.deal_number || "",
          start_date: deal.start_date
            ? new Date(deal.start_date)
            : createFormDateTime(),
          end_date: deal.end_date ? new Date(deal.end_date) : null,
          client_id: deal.client_id || "",
          asset_ids: assetIds,
          amount: deal.amount || 0,
          amount_due: deal.amount_due || 0,
          discount_amount: deal.discount_amount || 0,
          discount_type: deal.discount_type || "amount",
          status: deal.status || "в_процессе",
          actual_deposit: deal.actual_deposit || 0,
          deposit_date: deal.deposit_date || null,
        });

        // 🔍 ОТЛАДКА: Проверяем установленные данные скидки (старая система)
        console.log("🔍 УСТАНОВКА FORMDATA (старая система):", {
          deal_discount_amount: deal.discount_amount,
          deal_discount_type: deal.discount_type,
          setting_discount_amount: deal.discount_amount || 0,
          setting_discount_type: deal.discount_type || "amount",
        });

        if (assetIds.length > 0) {
          await loadAssetsByIds(assetIds);
        } else {
          setSelectedAssets([]);
        }

        // 👤 Загружаем клиента (старая система catch)
        if (deal.client_id) {
          await loadClientById(deal.client_id);
        }
      }

      // Загружаем платежи для этой сделки
      console.log("Платежи в deal.payments:", deal.payments);
      console.log("Платежи в deal.payment_summary:", deal.payment_summary);
      if (deal.payments) {
        console.log("Устанавливаем платежи:", deal.payments);
        setPayments(deal.payments);
      } else {
        console.log("Платежи не найдены в ответе сделки");
        setPayments([]);
      }

      // 💰 Сохраняем payment_summary с балансом
      if (deal.payment_summary) {
        console.log("💰 payment_summary получен:", deal.payment_summary);
        setPaymentSummary(deal.payment_summary);
      } else {
        console.log("⚠️ payment_summary отсутствует в ответе сделки");
        setPaymentSummary(null);
      }
      setDealReady(inventoryComplete);
    } catch (error) {
      setDealLoadError(true);
      console.error("Ошибка загрузки сделки:", error);
      enqueueSnackbar("Ошибка загрузки сделки", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  // Загрузка клиента по ID
  const loadClientById = async (clientId) => {
    try {
      console.log("🔍 Загружаем клиента с ID:", clientId);
      const response = await api.get(`/clients/${clientId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      console.log("✅ Клиент загружен:", response.data);
      setSelectedClient(response.data);
    } catch (error) {
      console.error("❌ Ошибка загрузки клиента:", error);
    }
  };

  // Загрузка активов по ID
  const loadAssetsByIds = async (assetIds) => {
    const loadedAssets = await Promise.all(assetIds.map(async (assetId) => {
      const response = await api.get(`/assets/rental/${assetId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!response.data || Number(response.data.id) !== Number(assetId)) {
        throw new Error("Неполные данные инвентаря");
      }
      return response.data;
    }));
    // A failed request must never be interpreted as removal from the deal.
    setSelectedAssets(loadedAssets);
  };

  // Функция для обновления статуса активов
  const updateAssetsStatus = async (assetIds, newStatus) => {
    try {
      for (const assetId of assetIds) {
        try {
          await api.patch(
            `/assets/rental/${assetId}`,
            { status: newStatus },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            },
          );
        } catch (error) {
          console.warn(`Не удалось обновить статус актива ${assetId}:`, error);
        }
      }

      // Перезагружаем список активов после обновления статусов
      await loadAssets();
    } catch (error) {
      console.error("Ошибка обновления статусов активов:", error);
    }
  };

  // Загрузка истории аренды для сделки
  const loadRentalHistory = async () => {
    if (isNewDeal) return; // Для новых сделок истории нет

    try {
      setHistoryLoading(true);
      setHistoryError(false);
      const response = await api.get(`/rental-history/deal/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setRentalHistory(response.data || []);
    } catch (error) {
      console.error("Ошибка загрузки истории аренды:", error);
      setHistoryError(true);
    } finally {
      setHistoryLoading(false);
    }
  };

  const applyPriceAdjustment = () => {
    const numericValue = Number(adjustmentForm.value);
    if (
      adjustmentForm.kind !== "none" &&
      (!Number.isFinite(numericValue) || numericValue < 0)
    ) {
      enqueueSnackbar("Укажите положительное значение корректировки", {
        variant: "error",
      });
      return;
    }
    if (
      adjustmentForm.valueType === "percent" &&
      adjustmentForm.kind === "discount" &&
      numericValue > 100
    ) {
      enqueueSnackbar("Скидка не может превышать 100%", { variant: "error" });
      return;
    }

    if (adjustmentForm.scope === "all") {
      const signedValue =
        adjustmentForm.kind === "none"
          ? 0
          : adjustmentForm.kind === "discount"
            ? -numericValue
            : numericValue;
      setFormData((prev) => ({
        ...prev,
        discount_type: adjustmentForm.valueType,
        discount_amount: signedValue,
      }));
      setItemAdjustments(
        Object.fromEntries(selectedAssets.map((asset) => [asset.id, 0])),
      );
      enqueueSnackbar("Корректировка применена ко всему заказу", {
        variant: "success",
      });
      return;
    }

    if (!adjustmentForm.assetIds.length) {
      enqueueSnackbar("Выберите хотя бы один товар", { variant: "error" });
      return;
    }
    const targetIds = new Set(adjustmentForm.assetIds.map(Number));
    const next = { ...itemAdjustments };
    for (const item of calculations.lineItems) {
      if (!targetIds.has(Number(item.assetId))) continue;
      const applied =
        adjustmentForm.kind === "none"
          ? 0
          : adjustmentForm.valueType === "percent"
            ? (item.baseAmount * numericValue) / 100
            : numericValue;
      if (adjustmentForm.kind === "discount" && applied > item.baseAmount) {
        enqueueSnackbar(`Скидка больше стоимости позиции «${item.name}»`, {
          variant: "error",
        });
        return;
      }
      next[item.assetId] =
        adjustmentForm.kind === "markup"
          ? -Math.round(applied * 100) / 100
          : Math.round(applied * 100) / 100;
    }
    // Общая и персональные корректировки взаимоисключающие: так скидка не
    // применяется к одной позиции дважды.
    setFormData((prev) => ({ ...prev, discount_amount: 0 }));
    setItemAdjustments(next);
    enqueueSnackbar("Корректировка применена к выбранным товарам", {
      variant: "success",
    });
  };

  const clearItemAdjustment = (item) => {
    setItemAdjustments((previous) => ({ ...previous, [item.assetId]: 0 }));
    enqueueSnackbar(
      `${item.discountApplied > 0 ? "Скидка" : "Наценка"} убрана из расчёта. Сохраните сделку, чтобы подтвердить изменение.`,
      { variant: "info" },
    );
  };

  const persistItemAdjustments = async (dealId) => {
    await api.put(
      `/rental-asset-deals/deal/${dealId}/price-adjustments`,
      {
        adjustments: selectedAssets.map((asset) => ({
          asset_id: asset.id,
          discount_applied: Number(itemAdjustments[asset.id] || 0),
        })),
      },
      {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      },
    );
  };

  // Сохранение сделки
  const handleSave = async () => {
    if (savingRef.current || isClosedDeal || loading || !dealReady) return;
    savingRef.current = true;
    setSaving(true);
    try {
      if (!formData.deal_number.trim()) {
        enqueueSnackbar("Номер сделки обязателен", { variant: "error" });
        return;
      }
      if (!formData.client_id) {
        enqueueSnackbar("Выберите клиента", { variant: "error" });
        return;
      }
      if (!formData.start_date) {
        enqueueSnackbar("Укажите дату начала", { variant: "error" });
        return;
      }
      if (formData.end_date && new Date(formData.end_date) < new Date(formData.start_date)) {
        enqueueSnackbar("Дата окончания не может быть раньше даты начала", { variant: "error" });
        return;
      }

      const dealData = {
        deal_number: formData.deal_number.trim(),
        start_date: convertDateToTimezoneISO(formData.start_date),
        end_date: convertDateToTimezoneISO(formData.end_date),
        client_id: parseInt(formData.client_id),
        asset_ids: formData.asset_ids,
        amount: parseFloat(calculations.totalAmount) || 0,
        amount_due: Math.max(0, parseFloat(calculations.remaining) || 0),
        discount_amount: parseFloat(formData.discount_amount) || 0,
        discount_type: formData.discount_type,
        status: formData.status,
        actual_deposit: parseFloat(formData.actual_deposit) || 0,
        deposit_date: formData.deposit_date,
      };

      // 🔍 ОТЛАДКА: Проверяем данные скидки перед сохранением
      console.log("🔍 СКИДКА ПЕРЕД СОХРАНЕНИЕМ:", {
        formData_discount_amount: formData.discount_amount,
        formData_discount_type: formData.discount_type,
        dealData_discount_amount: dealData.discount_amount,
        dealData_discount_type: dealData.discount_type,
        typeof_formData_discount_amount: typeof formData.discount_amount,
        parseFloat_result: parseFloat(formData.discount_amount),
      });

      console.log("🚀 ФОРМИРОВАНИЕ ДАННЫХ ДЛЯ СОХРАНЕНИЯ:");
      console.log(
        "🚀   calculations.finalAmount:",
        calculations.finalAmount,
        typeof calculations.finalAmount,
      );
      console.log(
        "🚀   parseFloat(calculations.finalAmount):",
        parseFloat(calculations.finalAmount),
      );
      console.log("🚀   dealData.amount:", dealData.amount);
      console.log("🚀   calculations объект целиком:", calculations);

      console.log("Сохранение сделки:", { isNewDeal, id, dealData });
      console.log(
        "Выбранные активы для сохранения:",
        selectedAssets.map((a) => ({ id: a.id, name: a.name })),
      );

      if (isNewDeal) {
        const response = await api.post("/rental-deals/", dealData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        console.log("Ответ при создании:", response.data);
        const createdDeal = response.data;

        // Создаем записи в новой таблице rental_asset_deals
        if (formData.asset_ids && formData.asset_ids.length > 0) {
          try {
            // Создаем отдельные записи для каждого актива с индивидуальными залогами
            for (let i = 0; i < formData.asset_ids.length; i++) {
              const assetId = formData.asset_ids[i];
              const asset = selectedAssets.find((a) => a.id === assetId);

              // 🎯 ИСПРАВЛЕНИЕ: Получаем правильную цену тарифа за период для каждого актива
              const assetTariff = getAssetTariff(asset);
              let rental_price_per_period = 0;
              let tariff_period = "день";

              if (assetTariff && assetTariff.price) {
                // Используем цену тарифа (это цена за ОДИН период, не за всю аренду)
                rental_price_per_period = parseFloat(assetTariff.price) || 0;

                // Определяем период тарифа на основе selected_tariff
                if (asset.selected_tariff) {
                  if (asset.selected_tariff.days > 0) {
                    tariff_period = "day";
                  } else if (asset.selected_tariff.hours > 0) {
                    tariff_period = "hour";
                  } else if (asset.selected_tariff.minutes > 0) {
                    tariff_period = "minute";
                  }
                }
              } else {
                // Fallback: используем старую логику деления общей суммы
                console.warn(
                  `⚠️ Тариф не найден для актива ${assetId}, используем fallback`,
                );
                rental_price_per_period =
                  parseFloat(calculations.finalAmount) /
                    formData.asset_ids.length || 0;
              }

              const assetDealData = {
                deal_id: createdDeal.id,
                asset_ids: [assetId], // Один актив
                start_date: convertDateToTimezoneISO(formData.start_date),
                end_date: convertDateToTimezoneISO(formData.end_date),
                rental_price_per_period: rental_price_per_period, // 🎯 Правильная цена за период
                deposit_amount: parseFloat(asset?.deposit) || 0, // Индивидуальный залог актива
                tariff_period: tariff_period, // 🎯 Правильный период тарифа
                notes: `Создано из сделки ${formData.deal_number}`,
              };

              console.log(
                `Создаем asset_deal для актива ${assetId}:`,
                assetDealData,
              );

              await api.post("/rental-asset-deals/bulk", assetDealData, {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
              });
            }

            console.log("✅ Asset deals созданы успешно для всех активов");
            await persistItemAdjustments(createdDeal.id);
          } catch (assetError) {
            console.warn("⚠️ Ошибка создания asset_deals:", assetError);
            enqueueSnackbar("Сделка создана, но состав активов не сохранён полностью. Проверьте её перед выдачей. Новые платежи не сохранены.", { variant: "error" });
            navigate(`/rental-deals/edit/${createdDeal.id}`);
            return;
          }
        }

        // Обновляем статус активов на "в аренде" для новой сделки
        await updateAssetsStatus(formData.asset_ids, "в аренде");

        // Сохраняем временные платежи на сервер
        const tempPayments = payments.filter((p) => p.temp);
        let failedPayments = 0;
        for (const payment of tempPayments) {
          try {
            const paymentData = {
              amount: payment.amount,
              payment_method: payment.payment_method,
              payment_date: payment.payment_date,
            };
            await api.post(
              `/rental-deals/${createdDeal.id}/payments`,
              paymentData,
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
              },
            );
          } catch (error) {
            failedPayments += 1;
            console.warn("Ошибка сохранения платежа:", error);
          }
        }

        if (failedPayments) {
          enqueueSnackbar(`Сделка создана, но платежи не сохранены: ${failedPayments}. Проверьте историю оплат перед повторным внесением.`, { variant: "error" });
        } else {
          enqueueSnackbar("Сделка успешно создана", { variant: "success" });
        }

        // Отправляем событие об обновлении данных
        window.dispatchEvent(new CustomEvent("rentalDealsUpdated"));

        // Перенаправляем на страницу редактирования созданной сделки
        if (createdDeal && createdDeal.id) {
          navigate(`/rental-deals/edit/${createdDeal.id}`);
        } else {
          // Если ID не получен, возвращаемся к списку
          setTimeout(() => {
            handleBack();
          }, 500);
        }
      } else {
        console.log(`Обновление сделки по URL: /rental-deals/${id}`);
        const response = await api.put(`/rental-deals/${id}`, dealData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        await persistItemAdjustments(id);
        console.log("Ответ при обновлении:", response.data);
        enqueueSnackbar("Сделка успешно обновлена", { variant: "success" });

        // Обновляем историю после сохранения
        loadRentalHistory();

        // Для обновленной сделки тоже обновляем статус активов
        await updateAssetsStatus(formData.asset_ids, "в аренде");

        // Отправляем событие об обновлении данных
        window.dispatchEvent(new CustomEvent("rentalDealsUpdated"));

        // Даем время на обработку события перед переходом
        setTimeout(() => {
          handleBack();
        }, 500);
      }
    } catch (error) {
      console.error("Ошибка сохранения сделки:", error);
      console.error("Детали ошибки:", error.response?.data);
      enqueueSnackbar(error.response?.data?.detail || "Ошибка сохранения сделки", { variant: "error" });
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  // Возврат на предыдущую страницу
  const handleBack = () => {
    navigate("/rental-deals");
  };

  // Форматирование даты с учетом часового пояса пункта выдачи
  const formatDateTimeForRentalPoint = (dateInput, includeTime = true) => {
    if (!dateInput) return "";

    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return "Некорректная дата";

    // Получаем часовой пояс из пункта выдачи или дефолтный браузерный
    const targetTimezone = getRentalPointTimezone();

    const options = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      timeZone: targetTimezone, // Используем часовой пояс пункта выдачи
    };

    if (includeTime) {
      options.hour = "2-digit";
      options.minute = "2-digit";
      options.second = "2-digit";
    }

    const formattedDate = date.toLocaleString("ru-RU", options);

    return formattedDate;
  };

  // Форматирование валюты
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("ru-RU", {
      style: "currency",
      currency: "RUB",
    }).format(amount);
  };

  // Добавление клиента
  const handleAddClient = () => {
    const clientUrl =
      "/clients/edit/new?returnTo=" +
      encodeURIComponent(window.location.pathname);
    window.open(clientUrl, "_blank");
  };

  // Обработчики для возврата актива
  const handleOpenReturnModal = (asset) => {
    setAssetToReturn(asset);
    setReturnModalOpen(true);
  };

  const handleCloseReturnModal = () => {
    setReturnModalOpen(false);
    setAssetToReturn(null);
  };

  const handleAssetReturned = async (assetId, returnData) => {
    try {
      // Обновляем состояние актива в локальном состоянии
      setSelectedAssets((prevAssets) =>
        prevAssets.map((asset) =>
          asset.id === assetId
            ? {
                ...asset,
                status: "возвращен",
                returned_date: returnData.returned_date,
              }
            : asset,
        ),
      );

      // Перезагружаем данные сделки для получения актуальных платежей
      if (!isNewDeal) {
        await loadDeal();
      }

      enqueueSnackbar("Актив успешно возвращен", { variant: "success" });
    } catch (error) {
      console.error("Ошибка при обновлении данных после возврата:", error);
      enqueueSnackbar("Ошибка при обновлении данных", { variant: "error" });
    }
  };

  // Добавление платежа
  const handleAddPayment = async () => {
    if (paymentSavingRef.current || isClosedDeal) return;
    console.log("🚀 НАЧАЛО handleAddPayment");
    console.log("🚀 paymentForm:", paymentForm);
    console.log("🚀 isNewDeal:", isNewDeal);

    if (!Number.isFinite(Number(paymentForm.amount)) || Number(paymentForm.amount) <= 0) {
      enqueueSnackbar("Введите корректную сумму платежа", { variant: "error" });
      return;
    }
    if (!paymentForm.date || Number.isNaN(new Date(paymentForm.date).getTime())) {
      enqueueSnackbar("Укажите дату платежа", { variant: "error" });
      return;
    }
    paymentSavingRef.current = true;
    setPaymentSaving(true);

    // Для новых сделок добавляем платеж в локальное состояние
    if (isNewDeal) {
      console.log("📝 Добавляем платеж в локальное состояние");
      console.log("📝 paymentForm.date:", paymentForm.date);
      console.log("📝 paymentForm.date type:", typeof paymentForm.date);
      console.log(
        "📝 paymentForm.date.toISOString():",
        paymentForm.date.toISOString(),
      );

      const newPayment = {
        id: Date.now(), // Временный ID для локального состояния
        amount: parseFloat(paymentForm.amount),
        payment_method: paymentForm.method,
        payment_date: paymentForm.date.toISOString(),
        temp: true, // Флаг что это временный платеж
      };

      console.log("📝 Созданный платеж:", newPayment);

      // Добавляем в локальное состояние
      setPayments((prev) => [newPayment, ...prev]);

      // Очищаем форму платежа
      setPaymentForm({
        amount: "",
        method: "cash",
        date: createFormDateTime(),
      });

      enqueueSnackbar(
        "Платеж добавлен. Сохраните сделку для применения изменений.",
        { variant: "success" },
      );
      paymentSavingRef.current = false;
      setPaymentSaving(false);
      return;
    }

    // Для существующих сделок отправляем на сервер
    try {
      const paymentData = {
        amount: parseFloat(paymentForm.amount),
        payment_method: paymentForm.method,
        payment_date: paymentForm.date.toISOString(),
      };

      const response = await api.post(
        `/rental-deals/${id}/payments`,
        paymentData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      const newPayment = response.data;

      // Обновляем список платежей
      setPayments((prev) => [newPayment, ...prev]);
      setPaymentSummary(prev => ({ ...prev, total_paid: Number(prev?.total_paid ?? payments.reduce((sum, p) => sum + Number(p.amount || 0), 0)) + Number(newPayment.amount) }));

      // Очищаем форму платежа
      setPaymentForm({
        amount: "",
        method: "cash",
        date: createFormDateTime(),
      });

      enqueueSnackbar("Платеж успешно добавлен", { variant: "success" });

      // Обновляем историю после добавления платежа
      loadRentalHistory();
    } catch (error) {
      console.error("Ошибка добавления платежа:", error);
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        "Ошибка добавления платежа";
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      paymentSavingRef.current = false;
      setPaymentSaving(false);
    }
  };

  // Удаление платежа
  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm("Вы уверены, что хотите удалить этот платеж?")) {
      return;
    }

    // Для временных платежей (новая сделка) просто удаляем из локального состояния
    const payment = payments.find((p) => p.id === paymentId);
    if (payment && payment.temp) {
      setPayments((prev) => prev.filter((payment) => payment.id !== paymentId));
      enqueueSnackbar("Платеж удален", { variant: "success" });
      return;
    }

    // Для существующих платежей отправляем запрос на сервер
    try {
      await api.delete(`/rental-deals/payments/${paymentId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      // Удаляем платеж из списка
      setPayments((prev) => prev.filter((payment) => payment.id !== paymentId));
      setPaymentSummary(prev => ({ ...prev, total_paid: Math.max(0, Number(prev?.total_paid ?? payments.reduce((sum, p) => sum + Number(p.amount || 0), 0)) - Number(payment?.amount || 0)) }));

      enqueueSnackbar("Платеж успешно удален", { variant: "success" });

      // Обновляем историю после удаления платежа
      loadRentalHistory();
    } catch (error) {
      console.error("Ошибка удаления платежа:", error);
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        "Ошибка удаления платежа";
      enqueueSnackbar(errorMessage, { variant: "error" });
    }
  };

  // Закрытие аренды
  const handleCloseRental = async () => {
    if (
      !window.confirm(
        "Вы уверены, что хотите закрыть аренду? Все активы будут возвращены и станут доступными.",
      )
    ) {
      return;
    }

    try {
      // Используем новый эндпоинт для закрытия сделки
      const response = await api.post(
        `/rental-deals/${id}/close`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      console.log("Сделка закрыта:", response.data);

      // Отправляем событие об обновлении данных
      window.dispatchEvent(new CustomEvent("rentalDealsUpdated"));

      enqueueSnackbar("Аренда успешно закрыта", { variant: "success" });
      handleBack();
    } catch (error) {
      console.error("Ошибка закрытия аренды:", error);
      const errorMessage =
        error.response?.data?.detail || "Ошибка закрытия аренды";
      enqueueSnackbar(errorMessage, { variant: "error" });
    }
  };

  // Фильтрованные активы (доступные для аренды и не выбранные уже)
  const filteredAssets = assets.filter((asset) => {
    // Проверяем статус (учитываем разный регистр)
    const status = (asset.status || "").toLowerCase();
    const isAvailable =
      status === "available" ||
      status === "свободен" ||
      status === "свободно" ||
      status === "доступен" ||
      status === "доступно";

    // Проверяем, не выбран ли уже этот актив
    const isNotSelected = !selectedAssets.some(
      (selectedAsset) => selectedAsset.id === asset.id,
    );

    // Проверяем принадлежность к выбранному пункту выдачи
    const isFromSelectedPoint =
      !selectedRentalPoint || asset.rental_point_id === selectedRentalPoint.id;

    return isAvailable && isNotSelected && isFromSelectedPoint;
  });

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Загрузка...</Typography>
      </Box>
    );
  }

  // Полноэкранная версия с HTML5 datetime-local вместо DatePicker
  return (
    <Box
      sx={{
        position: "fixed",
        top: "64px", // Отступ под хедер
        left: "200px", // Отступ под боковую панель
        right: 0,
        bottom: 0,
        backgroundColor: "#f5f5f5",
        overflow: "auto",
        zIndex: 1250, // Ниже хедера и сайдбара
      }}
    >
      {dealLoadError && (
        <Alert severity="error" action={<Button onClick={loadDeal}>Повторить загрузку</Button>}>
          Не удалось полностью загрузить сделку и инвентарь. Сохранение недоступно — повторите загрузку.
        </Alert>
      )}
      {/* Основное содержимое - два блока с самого верха */}
      {!isNewDeal && <DealReturnNotice dealId={id} onReturn={(assetId) => {
        const asset = selectedAssets.find(item => item.id === assetId);
        if (asset) handleOpenReturnModal(asset);
      }} />}
      <Box sx={{ display: "flex", minHeight: "calc(100vh - 64px)", mb: 10 }}>
        {/* Левая панель - Период аренды и Данные */}
        <Box
          sx={{
            flex: 1,
            backgroundColor: "white",
          }}
        >
          {/* Период аренды - точно по изображению */}
          <Box
            sx={{
              p: 2,
              borderBottom: "1px solid #e0e0e0",
              backgroundColor: "#f8f9fa",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
              Период аренды
            </Typography>

            {/* 🔒 Предупреждение для закрытых сделок */}
            {isClosedDeal && (
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  backgroundColor: "#fff3cd",
                  border: "1px solid #ffc107",
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: "#856404", fontWeight: "bold" }}
                >
                  🔒 Сделка закрыта. Редактирование недоступно.
                </Typography>
              </Box>
            )}

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {/* Поле "Начало" слева */}
              <Box sx={{ minWidth: "200px" }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Начало
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CalendarToday sx={{ mr: 1, color: "text.secondary" }} />
                  <TextField
                    disabled={isClosedDeal} // 🔒 Отключаем для закрытых сделок
                    type="datetime-local"
                    value={formatDateTimeLocal(formData.start_date)}
                    onChange={(e) => {
                      const newStartDate = parseDateTimeLocal(e.target.value);
                      setDatesDirty(true);
                      setFormData((prev) => {
                        // Если это новая сделка и поле "Конец" пустое, автоматически заполняем его тем же временем
                        const shouldAutoFillEndDate =
                          isNewDeal && !prev.end_date;

                        return {
                          ...prev,
                          start_date: newStartDate,
                          // Автоматически заполняем конец тем же временем, если он пуст
                          end_date: shouldAutoFillEndDate
                            ? newStartDate
                            : prev.end_date,
                        };
                      });
                    }}
                    variant="outlined"
                    sx={{
                      width: "200px",
                      "& .MuiInputBase-input": {
                        fontSize: "16px",
                        fontWeight: "bold",
                      },
                    }}
                  />
                </Box>
              </Box>

              {/* Поле "Конец" справа */}
              <Box sx={{ minWidth: "200px" }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Конец
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CalendarToday sx={{ mr: 1, color: "text.secondary" }} />
                  <TextField
                    disabled={isClosedDeal} // 🔒 Отключаем для закрытых сделок
                    type="datetime-local"
                    value={formatDateTimeLocal(formData.end_date)}
                    onChange={(e) => {
                      setDatesDirty(true);
                      setFormData((prev) => ({
                        ...prev,
                        end_date: parseDateTimeLocal(e.target.value),
                      }));
                    }}
                    variant="outlined"
                    sx={{
                      width: "200px",
                      "& .MuiInputBase-input": {
                        fontSize: "16px",
                        fontWeight: "bold",
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Остальной контент левой панели */}
          <Box sx={{ p: 2, pt: 1 }}>
            {/* Пункт выдачи */}
            {isNewDeal && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
                  📍 Пункт выдачи
                </Typography>
                <Autocomplete
                  options={rentalPoints}
                  getOptionLabel={(option) =>
                    `${option.name} (${option.address})`
                  }
                  value={selectedRentalPoint}
                  onChange={(event, newValue) => {
                    setSelectedRentalPoint(newValue);
                    // Сбрасываем выбранные активы при смене пункта
                    if (selectedAssets.length > 0) {
                      setSelectedAssets([]);
                      setFormData((prev) => ({ ...prev, asset_ids: [] }));
                    }
                  }}
                  loading={rentalPointsLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Выберите пункт выдачи"
                      variant="outlined"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {rentalPointsLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                {selectedRentalPoint && (
                  <Box
                    sx={{
                      mt: 1,
                      p: 1,
                      backgroundColor: "#f0f9ff",
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2" color="primary">
                      🌍 Часовой пояс: {selectedRentalPoint.timezone}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      📍 {selectedRentalPoint.address}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Клиент */}
            <Box sx={{ mb: 1.5 }}>
              {!isNewDeal && <Button variant="outlined" startIcon={<ChatIcon />} onClick={openClientChat}
                disabled={chatOpening || !savedClientId || String(savedClientId) !== String(formData.client_id)}
                sx={{ mb: 1.5 }}>
                {chatOpening ? 'Открываем чат…' : 'Чат с клиентом'}
              </Button>}
              {!isNewDeal && savedClientId && String(savedClientId) !== String(formData.client_id) &&
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Сохраните смену клиента перед открытием чата.</Typography>}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Клиент
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  disabled={isClosedDeal} // 🔒 Отключаем для закрытых сделок
                  sx={{ backgroundColor: "#00BCD4" }}
                  startIcon={<AddIcon />}
                  onClick={handleAddClient}
                >
                  Добавить клиента
                </Button>
              </Box>
              {console.log("🔍 RENDER: selectedClient =", selectedClient)}
              {console.log(
                "🔍 RENDER: formData.client_id =",
                formData.client_id,
              )}
              <Autocomplete
                disabled={isClosedDeal} // 🔒 Отключаем для закрытых сделок
                options={clients}
                filterOptions={(options) => options}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                loading={clientsLoading}
                loadingText="Поиск клиентов…"
                noOptionsText={clientsError || 'Клиенты не найдены'}
                onInputChange={(event, value, reason) => {
                  if (reason !== 'input' && reason !== 'clear') return;
                  clientQuery.current = value;
                  clearTimeout(clientTimer.current);
                  clientRequest.current += 1;
                  setClients([]);
                  setClientsLoading(true);
                  clientTimer.current = setTimeout(() => {
                    clientTimer.current = null;
                    loadClients();
                  }, 250);
                }}
                getOptionLabel={(option) => {
                  const label =
                    option.display_name ||
                    option.name ||
                    `${option.first_name || ""} ${
                      option.last_name || ""
                    }`.trim() ||
                    `Клиент #${option.id}`;
                  return label;
                }}
                value={selectedClient}
                onOpen={() => {
                  if (!clientTimer.current) loadClients();
                }}
                onChange={(event, newValue) => {
                  clearTimeout(clientTimer.current);
                  clientTimer.current = null;
                  clientQuery.current = '';
                  clientRequest.current += 1;
                  setClientsLoading(false);
                  setSelectedClient(newValue);
                  setFormData((prev) => ({
                    ...prev,
                    client_id: newValue?.id || "",
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Поиск клиента по имени"
                    variant="outlined"
                    fullWidth
                  />
                )}
                renderOption={(props, option) => {
                  const { key, ...otherProps } = props;
                  const displayName =
                    option.display_name ||
                    option.name ||
                    `${option.first_name || ""} ${
                      option.last_name || ""
                    }`.trim() ||
                    `Клиент #${option.id}`;
                  return (
                    <Box
                      key={key}
                      component="li"
                      {...otherProps}
                      sx={{ display: "flex", alignItems: "center", p: 2 }}
                    >
                      <Avatar sx={{ mr: 2, bgcolor: "#00BCD4" }}>
                        <Person />
                      </Avatar>
                      <Box>
                        <Typography variant="body1">{displayName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.contact_info ||
                            option.phone ||
                            "Нет контактной информации"}
                        </Typography>
                      </Box>
                    </Box>
                  );
                }}
              />

              {/* Паспортные данные — скрываемая секция */}
              {selectedClient && (
                <Accordion
                  defaultExpanded={false}
                  sx={{
                    mt: 2,
                    borderRadius: "8px !important",
                    border: "1px solid #e0e0e0",
                    boxShadow: "none",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      minHeight: 44,
                      "& .MuiAccordionSummary-content": { my: 0.5 },
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: "bold", color: "#2c3e50" }}
                    >
                      📋 Паспортные данные клиента
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 2 }}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(200px, 1fr))",
                        gap: 1.5,
                      }}
                    >
                      {selectedClient.passport_series && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Паспорт серии:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "medium" }}
                          >
                            {selectedClient.passport_series}{" "}
                            {selectedClient.passport_number || ""}
                          </Typography>
                        </Box>
                      )}
                      {selectedClient.passport_issued_by && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Кем выдан:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "medium" }}
                          >
                            {selectedClient.passport_issued_by}
                          </Typography>
                        </Box>
                      )}
                      {selectedClient.passport_issued_date && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Когда выдан:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "medium" }}
                          >
                            {new Date(
                              selectedClient.passport_issued_date,
                            ).toLocaleDateString("ru-RU")}
                          </Typography>
                        </Box>
                      )}
                      {selectedClient.address && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Адрес регистрации:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "medium" }}
                          >
                            {selectedClient.address}
                          </Typography>
                        </Box>
                      )}
                      {!selectedClient.passport_series &&
                        !selectedClient.passport_issued_by &&
                        !selectedClient.address && (
                          <Box sx={{ gridColumn: "1 / -1" }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontStyle: "italic" }}
                            >
                              Паспортные данные не заполнены. Рекомендуется
                              добавить их в профиле клиента.
                            </Typography>
                          </Box>
                        )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
                Инвентарь
              </Typography>
              <Autocomplete
                disabled={isClosedDeal} // 🔒 Отключаем для закрытых сделок
                multiple
                options={filteredAssets}
                getOptionLabel={(option) => {
                  let parts = [option.name || ""];
                  if (option.sku) parts.push(option.sku);
                  if (option.inventory_number)
                    parts.push(option.inventory_number);
                  return parts.join(", ");
                }}
                filterOptions={(options, { inputValue }) => {
                  const searchTerm = inputValue.toLowerCase();
                  return options.filter(
                    (option) =>
                      (option.name &&
                        option.name.toLowerCase().includes(searchTerm)) ||
                      (option.sku &&
                        option.sku.toLowerCase().includes(searchTerm)) ||
                      (option.inventory_number &&
                        option.inventory_number
                          .toLowerCase()
                          .includes(searchTerm)),
                  );
                }}
                value={selectedAssets}
                onChange={(event, newValue) => {
                  if (!isNewDeal && inventoryRemovalLocked.current &&
                      persistedAssetIds.current.some(assetId => !newValue.some(asset => asset.id === assetId))) {
                    enqueueSnackbar('Для сдачи вещи нажмите «Вернуть актив». Удаление не оформляет возврат.', { variant: 'warning' });
                    return;
                  }
                  console.log(
                    `🎯 ВЫБОР АКТИВОВ: изменение с`,
                    selectedAssets.length,
                    "на",
                    newValue.length,
                  );
                  console.log(
                    `🎯 ВЫБОР АКТИВОВ: новые активы:`,
                    newValue.map((a) => ({
                      id: a.id,
                      name: a.name,
                      selected_tariff: a.selected_tariff,
                      rental_tariff: a.rental_tariff,
                      tariff: a.tariff,
                      price: a.price,
                    })),
                  );

                  setSelectedAssets(newValue);
                  setFormData((prev) => ({
                    ...prev,
                    asset_ids: newValue.map((asset) => asset.id),
                  }));

                  console.log(
                    `🎯 ВЫБОР АКТИВОВ: обновлены asset_ids:`,
                    newValue.map((asset) => asset.id),
                  );
                }}
                onOpen={() => {
                  loadAssets();
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Поиск по названию, артикулу или инв. номеру"
                    helperText={!isNewDeal && inventoryRemovalLocked.current
                      ? 'Для сдачи вещи используйте «Вернуть актив» — стоимость и история сохранятся.' : undefined}
                    variant="outlined"
                    fullWidth
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    let chipLabel = option.name;
                    if (option.sku) chipLabel += ` (${option.sku})`;
                    return (
                      <Chip
                        variant="outlined"
                        label={chipLabel}
                        {...getTagProps({ index })}
                        {...(!isNewDeal && inventoryRemovalLocked.current && persistedAssetIds.current.includes(option.id)
                          ? { onDelete: undefined } : {})}
                        key={option.id}
                      />
                    );
                  })
                }
              />

              {/* Выбранные активы отображаются как карточки */}
              {selectedAssets.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {selectedAssets.map((asset) => {
                    const tariff = getAssetTariff(asset);
                    return (
                      <Paper
                        key={asset.id}
                        sx={{
                          p: 2,
                          mb: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                            {asset.name}
                          </Typography>
                          {asset.client_name && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: "primary.main",
                                fontWeight: "medium",
                                mb: 0.5,
                              }}
                            >
                              Клиент: {asset.client_name}
                              {asset.client_phone && ` • ${asset.client_phone}`}
                              {asset.client_contact_info &&
                                !asset.client_phone &&
                                ` • ${asset.client_contact_info}`}
                            </Typography>
                          )}
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                          >
                            {asset.rental_price_per_period
                              ? `${formatCurrency(
                                  asset.rental_price_per_period,
                                )} за период`
                              : tariff
                                ? `${formatCurrency(tariff.price)} за ${
                                    tariff.period
                                  }`
                                : "Тариф не определен"}
                          </Typography>
                          {asset.total_amount && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: "success.main",
                                fontWeight: "medium",
                                mb: 1,
                              }}
                            >
                              Общая сумма: {formatCurrency(asset.total_amount)}
                            </Typography>
                          )}
                          {asset.status === "возвращен" &&
                            asset.paid_amount != null && (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#2196f3",
                                  fontWeight: "medium",
                                  mb: 1,
                                }}
                              >
                                Оплачено: {formatCurrency(asset.paid_amount)}
                                {asset.return_comment &&
                                  ` (${asset.return_comment})`}
                              </Typography>
                            )}
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                          >
                            Сумма залога: {asset.deposit || 0} ₽
                          </Typography>
                          {asset.start_date && asset.end_date && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 2 }}
                            >
                              Период:{" "}
                              {formatDateTimeForRentalPoint(
                                asset.start_date,
                                false,
                              )}{" "}
                              -{" "}
                              {formatDateTimeForRentalPoint(
                                asset.end_date,
                                false,
                              )}
                            </Typography>
                          )}
                          <Box sx={{ display: "flex", gap: 1 }}>
                            {asset.status === "rented" ||
                            asset.status === "в аренде" ||
                            asset.status === "в_аренде" ||
                            asset.status === "возвращен" ? (
                              <Button
                                variant="contained"
                                size="small"
                                sx={{
                                  backgroundColor:
                                    asset.status === "возвращен"
                                      ? "#4caf50"
                                      : "#ff9800",
                                  "&:hover": {
                                    backgroundColor:
                                      asset.status === "возвращен"
                                        ? "#45a049"
                                        : "#e68900",
                                  },
                                }}
                                disabled={asset.status === "возвращен"}
                              >
                                {asset.status === "возвращен"
                                  ? `Возвращен ✓ ${
                                      asset.returned_date
                                        ? new Date(
                                            asset.returned_date,
                                          ).toLocaleDateString("ru-RU")
                                        : ""
                                    }`
                                  : "В аренде"}
                              </Button>
                            ) : (
                              <Button
                                variant="contained"
                                size="small"
                                sx={{ backgroundColor: "#4caf50" }}
                                disabled
                              >
                                Выбрано
                              </Button>
                            )}
                            {asset.status !== "возвращен" &&
                              formData.status !== "закрыта" &&
                              formData.status !== "отменена" && (
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => handleOpenReturnModal(asset)}
                                  sx={{
                                    backgroundColor: "#FF4444",
                                    color: "white",
                                    "&:hover": {
                                      backgroundColor: "#DD2222",
                                    },
                                  }}
                                >
                                  🔄 ВЕРНУТЬ АКТИВ
                                </Button>
                              )}
                          </Box>
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              )}
            </Box>

            {/* История аренды */}
            {!isNewDeal && (
              <Accordion
                defaultExpanded
                sx={{
                  mb: 3,
                  borderRadius: "8px !important",
                  border: "1px solid #e0e0e0",
                  boxShadow: "none",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: "#f8f9fa",
                    borderRadius: "8px",
                    minHeight: 44,
                    "& .MuiAccordionSummary-content": { my: 0.5 },
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    История аренды
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 2 }}>
                  {historyLoading ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", p: 2 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Загрузка истории...
                      </Typography>
                    </Box>
                  ) : historyError ? (
                    <Alert severity="error" action={
                      <Button color="inherit" onClick={loadRentalHistory}>Повторить загрузку истории</Button>
                    }>
                      Не удалось загрузить историю аренды
                    </Alert>
                  ) : rentalHistory.length === 0 ? (
                    <Paper sx={{ p: 2, textAlign: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        История пока пуста
                      </Typography>
                    </Paper>
                  ) : (
                    <Box role="feed" aria-label="События аренды" sx={{ maxHeight: 520, overflowY: "auto" }}>
                      {[...rentalHistory].reverse().map((historyItem) => (
                        <Paper
                          key={historyItem.id}
                          sx={{
                            p: 2,
                            mb: 1,
                            borderLeft: "4px solid",
                            borderLeftColor:
                              historyItem.event_type === "СОЗДАНИЕ"
                                ? "#4caf50"
                                : historyItem.event_type === "ПЛАТЕЖ"
                                  ? "#2196f3"
                                  : historyItem.event_type ===
                                      "ИЗМЕНЕНИЕ_АКТИВОВ"
                                    ? "#ff9800"
                                    : historyItem.event_type === "ЗАКРЫТИЕ"
                                      ? "#9c27b0"
                                      : historyItem.event_type === "ОТМЕНА"
                                        ? "#f44336"
                                        : "#757575",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              mb: 1,
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: "bold" }}
                            >
                              {/* Для платежей показываем правильный заголовок и убираем упоминание номера сделки */}
                              {historyItem.event_type === "ПЛАТЕЖ"
                                ? (String(historyItem.title || '').startsWith(
                                    "Получен платеж",
                                  )
                                    ? historyItem.title
                                    : historyItem.description ||
                                      historyItem.title || 'Платёж'
                                  ).replace(/\s+по сделке\s+[A-Z0-9-]+/gi, "")
                                : historyItem.title}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatDateTimeForRentalPoint(
                                historyItem.event_date,
                                true,
                              )}
                            </Typography>
                          </Box>

                          {/* Для платежей не показываем description, если title уже содержит полную информацию */}
                          {historyItem.description &&
                            historyItem.event_type !== "ПЛАТЕЖ" && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 1 }}
                              >
                                {historyItem.description}
                              </Typography>
                            )}

                          {/* Убираем дублирующую сумму для платежей */}
                          {historyItem.amount != null &&
                            !historyItem.extension_document && historyItem.event_type !== "ПЛАТЕЖ" && (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "success.main",
                                  fontWeight: "medium",
                                  mb: 1,
                                }}
                              >
                                Сумма: {historyItem.amount} ₽
                              </Typography>
                            )}

                          {/* Убираем дублирующую информацию о клиенте */}
                          {historyItem.client_name &&
                            historyItem.event_type !== "ПЛАТЕЖ" && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 1 }}
                              >
                                Клиент: {historyItem.client_name}
                              </Typography>
                            )}

                          {historyItem.assets_info &&
                            Array.isArray(historyItem.assets_info) &&
                            historyItem.assets_info.length > 0 && (
                              <Box sx={{ mb: 1 }}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {historyItem.new_data?.action === "удаление_активов"
                                    ? "Удалены активы: "
                                    : historyItem.event_type === "СОЗДАНИЕ" ||
                                        historyItem.new_data?.action === "добавление_активов"
                                      ? "Добавлены активы: "
                                      : "Активы: "}
                                  {historyItem.assets_info
                                    .map((asset) => asset.name || asset)
                                    .join(", ")}
                                </Typography>
                              </Box>
                            )}

                          <Typography
                            variant="caption"
                            sx={{ color: "primary.main" }}
                          >
                            Автор: {historyItem.user_name}
                          </Typography>
                          <RentalHistoryDetails event={historyItem} formatDate={date => formatDateTimeForRentalPoint(date, false)} />
                        </Paper>
                      ))}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        </Box>

        {/* Правая панель - Чек и калькуляция */}
        <Box
          sx={{
            width: 400,
            backgroundColor: "white",
            borderLeft: "1px solid #e0e0e0",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Цветной заголовок Чека */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
              px: 2,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", color: "white" }}
            >
              Расчёт аренды{" "}
              {formData.deal_number
                ? `№${formData.deal_number}`
                : "(новая сделка)"}
            </Typography>
            <Chip
              label={getStatusLabel(formData.status)}
              size="small"
              sx={{ ...getStatusChipStyle(formData.status), fontWeight: 700 }}
            />
          </Box>

          <Box sx={{ p: 2, flex: 1, overflowY: "auto" }}>
            {/* Чек */}
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
              >
                <Typography variant="body2">Максимальный срок</Typography>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  {calculations.duration} дней ({calculations.weeks} недель)
                </Typography>
              </Box>
              {calculations.lineItems.map((item) => (
                <Paper
                  key={item.assetId}
                  variant="outlined"
                  sx={{ p: 1.25, mb: 1, borderRadius: 2 }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {item.name}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                      {formatCurrency(item.finalAmount)}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {formatDateTimeForRentalPoint(item.startDate, false)} — {formatDateTimeForRentalPoint(item.endDate, false)}
                  </Typography>
                  {item.discountApplied !== 0 && (
                    <Box sx={{ display: "flex", alignItems: "center", color: item.discountApplied > 0 ? "success.main" : "warning.main" }}>
                      <Typography variant="caption">
                        {formatCurrency(item.baseAmount)} {item.discountApplied > 0 ? "− скидка" : "+ наценка"} {formatCurrency(Math.abs(item.discountApplied))}
                      </Typography>
                      <IconButton
                        size="small"
                        disabled={isClosedDeal}
                        aria-label={`${item.discountApplied > 0 ? "Убрать скидку" : "Убрать наценку"} для ${item.name}`}
                        title={item.discountApplied > 0 ? "Убрать скидку" : "Убрать наценку"}
                        onClick={() => clearItemAdjustment(item)}
                        sx={{ ml: 0.25, p: 0.25, color: "inherit" }}
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  )}
                </Paper>
              ))}
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
              >
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>Сумма аренды</Typography>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  {formatCurrency(calculations.totalAmount)}
                </Typography>
              </Box>
              {Number(formData.discount_amount) !== 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Typography variant="body2">
                    {Number(formData.discount_amount) < 0 ? "Скидка на заказ" : "Наценка на заказ"}
                    {formData.discount_type === "percent" ? ` (${Math.abs(Number(formData.discount_amount))}%)` : ""}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "bold",
                      color:
                        formData.discount_amount < 0 ? "#4caf50" : "#ff9800",
                    }}
                  >
                    {formData.discount_type === "percent"
                      ? formatCurrency(
                          (calculations.totalAmount *
                            formData.discount_amount) /
                            100,
                        )
                      : formatCurrency(formData.discount_amount)}
                  </Typography>
                </Box>
              )}
              {calculations.finalAmount !== calculations.totalAmount && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    Итого к оплате
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {formatCurrency(calculations.finalAmount)}
                  </Typography>
                </Box>
              )}
              {!isNewDeal && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Typography variant="body2">Оплачено</Typography>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {formatCurrency(calculations.paid)}
                  </Typography>
                </Box>
              )}
              {!isNewDeal && <BankRefundsPanel dealId={id} refreshKey={formData.amount} onChanged={loadDeal} />}
              <Divider sx={{ my: 2 }} />
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
              >
                <Typography variant="body2">Общий залог</Typography>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  {formatCurrency(calculations.totalDeposit)}
                </Typography>
              </Box>
              {!isNewDeal && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 2,
                    p: 2,
                    backgroundColor:
                      calculations.remaining > 0 ? "#fff3e0" : "#e8f5e9",
                    borderRadius: 2,
                    border: `1px solid ${calculations.remaining > 0 ? "#ff9800" : "#4caf50"}`,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      color: calculations.remaining > 0 ? "#e65100" : "#2e7d32",
                    }}
                  >
                    {calculations.remaining > 0
                      ? "К доплате"
                      : calculations.remaining < 0
                        ? "Переплата"
                        : "Расчёт закрыт"}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: "bold",
                      color: calculations.remaining > 0 ? "#e65100" : "#2e7d32",
                    }}
                  >
                    {formatCurrency(Math.abs(calculations.remaining))}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Залог */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Залог
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 2,
                  p: 2,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2">
                  Рекомендуемая сумма залога:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "bold", color: "#1976d2" }}
                >
                  {formatCurrency(calculations.totalDeposit)}
                </Typography>
              </Box>

              <TextField
                disabled={isClosedDeal} // 🔒 Отключаем для закрытых сделок
                fullWidth
                label="Фактическая сумма залога"
                type="number"
                value={formData.actual_deposit}
                onChange={(e) => {
                  const value = parseFloat(e.target.value) || 0;
                  setFormData((prev) => ({
                    ...prev,
                    actual_deposit: value,
                    // Автоматически устанавливаем дату внесения залога, если она не установлена
                    deposit_date:
                      value > 0 && !prev.deposit_date
                        ? new Date().toISOString().slice(0, 16)
                        : prev.deposit_date,
                  }));
                }}
                sx={{ mb: 2 }}
                helperText="Введите фактически взятую сумму залога"
              />

              <TextField
                disabled={isClosedDeal} // 🔒 Отключаем для закрытых сделок
                fullWidth
                label="Дата внесения залога"
                type="datetime-local"
                value={formData.deposit_date || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    deposit_date: e.target.value,
                  }))
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />

              {/* Отображение введенного залога */}
              {formData.actual_deposit > 0 && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: "#e8f5e8",
                    borderRadius: 1,
                    border: "1px solid #4CAF50",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: "bold", color: "#2e7d32" }}
                    >
                      ✅ Внесенный залог:{" "}
                      {formatCurrency(formData.actual_deposit)}
                    </Typography>
                    {formData.deposit_date && (
                      <Typography variant="caption" color="text.secondary">
                        Дата внесения:{" "}
                        {new Date(formData.deposit_date).toLocaleDateString()}{" "}
                        {new Date(formData.deposit_date).toLocaleTimeString()}
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    size="small"
                    disabled={isClosedDeal} // 🔒 Отключаем для закрытых сделок
                    sx={{ color: "#f44336" }}
                    onClick={() => {
                      setFormData((prev) => ({
                        ...prev,
                        actual_deposit: 0,
                        deposit_date: null,
                      }));
                    }}
                    title="Удалить залог"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>

            {/* Скидка / наценка */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Скидка / наценка
              </Typography>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="adjustment-scope-label">Кому применить</InputLabel>
                <Select
                  labelId="adjustment-scope-label"
                  id="adjustment-scope"
                  disabled={isClosedDeal}
                  value={adjustmentForm.scope}
                  onChange={(e) =>
                    setAdjustmentForm((prev) => ({
                      ...prev,
                      scope: e.target.value,
                    }))
                  }
                  label="Кому применить"
                >
                  <MenuItem value="all">Весь заказ</MenuItem>
                  <MenuItem value="selected">Выбранные товары</MenuItem>
                </Select>
              </FormControl>

              {adjustmentForm.scope === "selected" && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="adjustment-assets-label">Товары</InputLabel>
                  <Select
                    labelId="adjustment-assets-label"
                    id="adjustment-assets"
                    multiple
                    disabled={isClosedDeal}
                    value={adjustmentForm.assetIds}
                    onChange={(e) => setAdjustmentForm((prev) => ({
                      ...prev,
                      assetIds: typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value,
                    }))}
                    label="Товары"
                    renderValue={(ids) => ids.map((assetId) =>
                      selectedAssets.find((asset) => Number(asset.id) === Number(assetId))?.name || `Товар #${assetId}`
                    ).join(", ")}
                  >
                    {selectedAssets.map((asset) => (
                      <MenuItem key={asset.id} value={asset.id}>{asset.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 2 }}>
                <FormControl fullWidth>
                  <InputLabel id="adjustment-kind-label">Операция</InputLabel>
                  <Select
                    labelId="adjustment-kind-label"
                    id="adjustment-kind"
                    disabled={isClosedDeal}
                    value={adjustmentForm.kind}
                    onChange={(e) => setAdjustmentForm((prev) => ({ ...prev, kind: e.target.value }))}
                    label="Операция"
                  >
                    <MenuItem value="discount">Скидка</MenuItem>
                    <MenuItem value="markup">Наценка</MenuItem>
                    <MenuItem value="none">Без корректировки</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel id="adjustment-value-type-label">Формат</InputLabel>
                  <Select
                    labelId="adjustment-value-type-label"
                    id="adjustment-value-type"
                    disabled={isClosedDeal || adjustmentForm.kind === "none"}
                    value={adjustmentForm.valueType}
                    onChange={(e) => setAdjustmentForm((prev) => ({ ...prev, valueType: e.target.value }))}
                    label="Формат"
                  >
                    <MenuItem value="amount">Рубли</MenuItem>
                    <MenuItem value="percent">Проценты</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <TextField
                disabled={isClosedDeal || adjustmentForm.kind === "none"}
                fullWidth
                label={adjustmentForm.valueType === "percent"
                  ? "Процент"
                  : adjustmentForm.scope === "selected"
                    ? "Сумма для каждого товара"
                    : "Сумма"}
                type="number"
                value={adjustmentForm.value}
                onChange={(e) => setAdjustmentForm((prev) => ({ ...prev, value: e.target.value }))}
                inputProps={{ min: 0, step: adjustmentForm.valueType === "percent" ? 1 : 0.01 }}
                sx={{ mb: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {adjustmentForm.valueType === "percent" ? "%" : "₽"}
                    </InputAdornment>
                  ),
                }}
                helperText={adjustmentForm.scope === "selected"
                  ? "Для нескольких выбранных товаров сумма применяется к каждому; процент считается от каждой позиции."
                  : "Для всего заказа сумма или процент применяются один раз к общему итогу."}
              />
              <Button
                fullWidth
                variant="outlined"
                disabled={isClosedDeal || selectedAssets.length === 0}
                onClick={applyPriceAdjustment}
              >
                Применить корректировку
              </Button>
            </Box>

            {/* Платежи */}
            <Accordion
              defaultExpanded={true}
              sx={{
                mb: 3,
                borderRadius: "8px !important",
                border: "1px solid #e0e0e0",
                boxShadow: "none",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: "#f8f9fa",
                  borderRadius: "8px",
                  minHeight: 44,
                  "& .MuiAccordionSummary-content": {
                    my: 0.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  },
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Платежи
                </Typography>
                {payments.length > 0 && (
                  <Chip
                    label={payments.length}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      backgroundColor: "#e8f5e9",
                      color: "#2e7d32",
                      borderRadius: "8px",
                    }}
                  />
                )}
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2 }}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ fontWeight: "bold", mb: 1.5 }}
                >
                  Добавить платеж
                </Typography>

                <TextField
                  disabled={isClosedDeal} // 🔒 Отключаем для закрытых сделок
                  fullWidth
                  label="Сумма платежа"
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => {
                    console.log("Изменение суммы:", e.target.value);
                    setPaymentForm((prev) => ({
                      ...prev,
                      amount: e.target.value,
                    }));
                  }}
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₽</InputAdornment>
                    ),
                  }}
                />

                <TextField
                  disabled={isClosedDeal} // 🔒 Отключаем для закрытых сделок
                  fullWidth
                  label="Дата платежа"
                  type="datetime-local"
                  value={formatDateTimeLocal(paymentForm.date)}
                  onChange={(e) =>
                    setPaymentForm((prev) => ({
                      ...prev,
                      date: parseDateTimeLocal(e.target.value),
                    }))
                  }
                  sx={{ mb: 2 }}
                />

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Способ оплаты</InputLabel>
                  <Select
                    disabled={isClosedDeal} // 🔒 Отключаем для закрытых сделок
                    value={paymentForm.method}
                    onChange={(e) =>
                      setPaymentForm((prev) => ({
                        ...prev,
                        method: e.target.value,
                      }))
                    }
                    label="Способ оплаты"
                  >
                    <MenuItem value="cash">Наличные</MenuItem>
                    <MenuItem value="card">Карта</MenuItem>
                    <MenuItem value="transfer">Перевод</MenuItem>
                  </Select>
                </FormControl>

                <Button
                  fullWidth
                  variant="contained"
                  disabled={
                    isClosedDeal || // 🔒 Отключаем для закрытых сделок
                    paymentSaving ||
                    !paymentForm.amount ||
                    parseFloat(paymentForm.amount) <= 0
                  }
                  sx={{ backgroundColor: "#00BCD4" }}
                  onClick={handleAddPayment}
                >
                  Добавить платеж
                </Button>

                {/* Список платежей */}
                {payments.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Divider sx={{ mb: 2 }} />
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: "bold", mb: 1.5 }}
                    >
                      Список платежей
                    </Typography>
                    {payments.map((payment) => (
                      <Box
                        key={payment.id}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          p: 1,
                          mb: 1,
                          backgroundColor: "#f8f9fa",
                          borderRadius: 1,
                          border: "1px solid #e0e0e0",
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "bold" }}
                          >
                            {formatCurrency(payment.amount)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(payment.transaction_date ||
                              payment.payment_date) &&
                            new Date(
                              payment.transaction_date || payment.payment_date,
                            ).getTime()
                              ? new Date(
                                  payment.transaction_date ||
                                    payment.payment_date,
                                ).toLocaleDateString()
                              : "Дата не указана"}{" "}
                            -{" "}
                            {payment.payment_method === "cash"
                              ? "Наличные"
                              : payment.payment_method === "card"
                                ? "Карта"
                                : "Перевод"}
                            {payment.temp && " (не сохранен)"}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          disabled={isClosedDeal} // 🔒 Отключаем для закрытых сделок
                          sx={{ color: "#f44336" }}
                          onClick={() => handleDeletePayment(payment.id)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>
      </Box>

      {/* Нижние кнопки */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          p: 2,
          borderTop: "1px solid rgba(224, 224, 224, 0.8)",
          display: "flex",
          justifyContent: "center",
          gap: 2,
          boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
        }}
      >
        <Button
          variant="outlined"
          size="large"
          onClick={handleBack}
          sx={{ minWidth: 120 }}
        >
          Назад
        </Button>
        {!isNewDeal && !isClosedDeal && (
          <Button
            variant="contained"
            size="large"
            color="error"
            onClick={handleCloseRental}
            sx={{ minWidth: 120 }}
          >
            Закрыть аренду
          </Button>
        )}
        <Button
          variant="contained"
          size="large"
          onClick={handleSave}
          disabled={isClosedDeal || saving || loading || !dealReady}
          sx={{ backgroundColor: "#00BCD4", minWidth: 120 }}
        >
          Сохранить
        </Button>
      </Box>

      {/* Модальное окно возврата актива */}
      <AssetReturnModal
        open={returnModalOpen}
        onClose={handleCloseReturnModal}
        dealId={id}
        asset={assetToReturn}
        onAssetReturned={handleAssetReturned}
      />
    </Box>
  );
};

export default RentalDealsEditPage;
